//Library Imports
import React, { useState, useEffect, useReducer } from "react";
import { Space, Select, Radio, Divider, Switch, Tag, Typography, Tooltip } from "antd";
import { useSelector, shallowEqual } from "react-redux";
import store, { RootState } from "../../../store/index";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

//Local Imports
import Input from "../../Common/Components/Input";
import Checkbox from "../../Common/Components/Checkbox";
import SmallButton from "../../Content/Shared/SmallButton";
import ConfirmDialog from "../../Common/Components/ConfirmDialog";
import SendingWhite from "../../../assets/img/icons/SendingWhite.svg";
import {
  closeSendConfirmationBox,
  getPublishData,
  sendPublishData,
  scheduleEmail,
  saveEmailData,
  dispatchErrorInSaveState,
  resetLoadedStateOfPublishData,
  getRecipientCount,
  sendEmailWithOutlook,
  resetOutlookConnection,
} from "../actions";
import { closeOverlay, openOverlay, setMainNavBackOverlay, setStickyHeaderBackOverlay } from "../../GlobalActions";
import FloatingSidebarTwo from "modules/Common/Components/FloatingSidebarTwo";
import { IPublishData, IScheduleState } from "./../interfaces";
import { getQueryEmailId } from "./../../EmailBrowser/utils";
import { ASSET_AUDIENCE, DESIGN_PREVIEW, EMAIL_WITH_OUTLOOK, PROMO_CODES, PUBLISH } from "constants/actionTypes";
import { getQueryCampaignId } from "modules/EmailBrowser/utils";
import { getRecommendedLists, getRecommendedPeople } from "modules/Campaigns/CampaignAssets/actions";
import { removeGillRoyIcon, getPlanType } from "utils";
import {
  getContactsForAsset,
  getListForAsset,
  searchListContacts,
  updateCampaignRecipientState,
} from "modules/Campaigns/Audience/actions";
import { ReactComponent as Group } from "../../../assets/img/icons/Group.svg";
import { ReactComponent as RemoveCross } from "../../../assets/img/icons/RemoveCross.svg";
import { ReactComponent as ListIconOrange } from "../../../assets/img/icons/ListIconOrange.svg";
import ImportListFlow from "modules/Campaigns/Audience/Import/importListFlow";
import DropDownArrowIcon from "modules/Common/Components/DropDownArrowIcon";
import { validateEmailAddress } from "modules/Campaigns/Audience/People/Utils";
import Message from "modules/Common/Components/Message";
import { updateEmailNameForBrowserEmailList } from "modules/EmailBrowser/actions";
import { EMAIL_LIST } from "constants/types";
import { stat } from "fs";
import { setReminderToPublishDigest } from "modules/Campaigns/Emails/actions";

const { Paragraph } = Typography;

const PublishContent: React.FC = () => {
  // Redux States
  const publishData: IPublishData = useSelector((state: RootState) => state.publish.data);
  const publishDataLoaded: boolean = useSelector((state: RootState) => state.publish.loaded);
  const publishDataLoading: boolean = useSelector((state: RootState) => state.publish.loading);
  const isInternalUser: boolean = useSelector((state: RootState) => state.user.userData.xiq_internal_user);
  // Show Confirmation after sending
  const openPublishedConfirmation = useSelector(
    (state: RootState) => state.publish.openPublishedConfirmation,
    shallowEqual
  );
  const isPremium = useSelector((state: RootState) => state.user.isPremium);
  const outlookSenderEmailAddress: string = useSelector(
    (state: RootState) => state.publish.outlookSenderEmailAddress
  );
  const outlookSenderName: string = useSelector((state: RootState) => state.publish.outlookSenderName);
  const callbackSuccessState: string = useSelector((state: RootState) => state.publish.callbackSuccessState);
  const isAuthorized: boolean = useSelector((state: RootState) => state.publish.isAuthorized);
  const outlookRemainingEmails: number = useSelector((state: RootState) => state.publish.outlookRemainingEmails);
  const disableSendFrom: boolean = useSelector((state: RootState) => state.publish.disableSendFrom);
  const suggestedEmails = useSelector((state: RootState) => state.suggestedEmails.suggestedContacts);
  const suggestedEmailsLoading = useSelector((state: RootState) => state.suggestedEmails.loading);
  const searchErrorMessage = useSelector((state: RootState) => state.suggestedEmails.message);
  const recommendedPeopleLength = useSelector((state: RootState) => state.recommendedPeople.data.length);
  const recommendedListLength = useSelector((state: RootState) => state.recommendedLists.data.length);
  const total_recipients: string | number = useSelector(
    (state: RootState) => state.viewRecipients.total_recipients
  );
  const recipientLoading: boolean = useSelector((state: RootState) => state.viewRecipients.loading);
  const recommendedPeople = useSelector((state: RootState) => state.recommendedPeople.data);
  const campaignAudienceLists = useSelector((state: RootState) => state.campaignAudience.data.lists);
  const campaignAudienceContacts = useSelector((state: RootState) => state.campaignAudience.data.contacts);
  const editionStatus: any = useSelector((state: RootState) => state.editionData.editionStatus);
  const emailType: any = useSelector((state: RootState) => state.editionData.emailType);
  const userEmail: string = useSelector((state: RootState) => state.publish.userEmail);
  const userName: string = useSelector((state: RootState) => state.publish.userName);
  // for updating subject when changed from Header
  const editionName: string = useSelector((state: RootState) => state.editionData.editionName);
  const outlookRouteReducerState: boolean = useSelector((state: RootState) => state.publish.outlookRoute); // state uodated via outllok connection call made by user
  const outlookRoutePublishAPI: boolean = useSelector((state: RootState) => state.publish.outlookRoutePublishAPI); // state updated via publish API call
  const assetType: any = useSelector((state: RootState) => state.design.data.asset_type);
  const emailWithOutlookLoading: boolean = useSelector(
    (state: RootState) => state.publish.emailWithOutlookLoading
  );
  const outlookConnectionError: boolean = useSelector((state: RootState) => state.publish.connectionError);
  const { first_name, last_name } = useSelector((state: RootState) => state.user.userData);
  const exportPromoCodesSuccess: boolean = useSelector(
    (state: RootState) => state.publish.exportPromoCodesSuccess
  );
  const workflowCurrentState: string = useSelector((state: RootState) => state.editionData.workflowCurrentState);
  const workflowEmailStatus: string = useSelector((state: RootState) => state.editionData.workflowStatus);
  const workflowId: number = useSelector((state: RootState) => state.editionData.workflowId);
  const isXiqEventState: boolean = useSelector((state: RootState) => state.publish.data.is_event);

  // Local States
  const [scheduleSend, setScheduleSend] = useState(false); // let unListen: any = undefined;
  const [sendToObjArray, setSendToObjectArray] = useState<any>([]);
  // for schedule change check, we need to save the value for later comparison in local timezone
  const [scheduledTime, setScheduledTime] = useState("");
  // values for sending emails
  const [values, setValues] = useState({
    isTestEmail: false,
    isXiqEvent: isXiqEventState, // flag for webinar or events
    emailSubject: "",
    senderName: "",
    sendTo: "",
    sendFrom: "Select Email",
    sendWith: "1",
    sameReplyTo: true,
    replyTo: "",
    emailComplianceCheck: false,
    emailId: "",
    campaignId: "",
    includeAudience: true,
    isScheduled: false,
  });
  const [errors, setErrors] = useState({
    emailSubject: false,
    senderName: false,
    // sendTo: false,
    sendFrom: false,
    replyTo: false,
    isSendToFieldEmpty: false,
    enterValidEmail: false,
    sendWith: false,
    pastScheduleDateCheck: false,
  });
  const [replyToEmailCheck, setReplyToEmailCheck] = useState<boolean>(publishData.reply_to_status);
  const [showRecipientAsider, setShowRecipientAsider] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDraftWorkflowPopup, setShowDraftWorkflowPopup] = useState(false);
  const [showRecipeintsZeroDialog, setShowRecipientsZeroDialog] = useState(false);
  const [mailToCount, setMailToCount] = useState(0);
  // const [importedListContactCount, setImportedListContactCount] = useState(0);

  const [showImportListAsider, setShowImportListAsider] = useState(false);
  const [firstTimeLoad, setFirstTimeLoad] = useState(false);
  const [sendToSearchValue, setSendToSearchValue] = useState("");
  const [showCallbackError, setShowCallbackError] = useState(true);
  const [subjectRef, setSubjectRef] = useState(publishData.subject);
  const [senderName, setSenderName] = useState(publishData.sender_name);
  const [recipientCheckBoxState, setRecipientCheckBoxState] = useState(publishData?.include_audience ?? false);
  const [showAudienceAsider, setShowAudienceAsider] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState(Array<number>());
  const [selectedListIds, setSelectedListIds] = useState(Array<number>());
  const [selectedContactNames, setSelectedContactNames] = useState(Array<string>());
  const [selectedContactEmails, setSelectedContactEmails] = useState(Array<string>());
  const [selectedListNames, setSelectedListNames] = useState(Array<string>());
  const [sendDisabled, setSendDisabled] = useState(false);
  const [openStateDropdown, setOpenStateDropdown] = useState(false);
  const [recipientCountState, setRecipientCountState] = useState<number | string>("fetching");
  const [outlookSuccessInfoMessage, setOutlookSuccessInfoMessage] = useState(false);
  const [emailWithOutlookConfirmation, setEmailWithOutlookConfirmation] = useState(false);
  const [firstMount, setFirstMount] = useState(false);
  const [showExportSuccessPopup, setShowExportSuccessPopup] = useState(false);

  // Local Variables
  const emailIdOfDraftEmail = getQueryEmailId();
  const history = useHistory(); // useHistory from react-router for redirection
  const monthsMap: any = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sept: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };
  const { Option } = Select;
  // regex for checking if the email address is correct
  const regexp = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
  const disablePublishBtn = getPlanType()?.toUpperCase() === "ENTERPRISE" ? false : true;
  const EmailScheduleActions = {
    UPDATE_DAY: "UPDATE_DAY",
    UPDATE_MONTH: "UPDATE_MONTH",
    UPDATE_YEAR: "UPDATE_YEAR",
    UPDATE_HOUR: "UPDATE_HOUR",
    UPDATE_MINUTE: "UPDATE_MINUTE",
    UPDATE_PERIOD: "UPDATE_PERIOD",
  };
  const monthsCollection = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

  // Component Logic
  const openRecipientsSidebar = () => {
    setShowAudienceAsider(false);
    setShowRecipientAsider(true);
  };

  const hideRecipientAsider = (loadingState: boolean) => {
    setShowRecipientAsider(false);
    if (!loadingState) {
      getListForAsset(getQueryCampaignId());
      getContactsForAsset("", "", getQueryCampaignId(), 0, 22, "", true, true, true);
    }
  };

  const validateEmail = (email: string) => {
    if (!email) {
      return false;
    }
    return email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  useEffect(() => {
    if (isXiqEventState) {
      setValues({ ...values, isXiqEvent: true });
    } else {
      setValues({ ...values, isXiqEvent: false });
    }
  }, [isXiqEventState]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      handleConfirmExportSalesXPopup();
    };
  }, []);

  // changing the emailSubject when changed from Header
  useEffect(() => {
    setValues((state) => ({ ...state, emailSubject: editionName }));
    setSubjectRef(editionName);
  }, [editionName]);

  useEffect(() => {
    if (publishDataLoaded === false) {
      return;
    }
    populateEmailPlatform(publishData.email_platform);
    populateContactAndListIds([
      ...(publishData.selected_contacts ?? []),
      ...(publishData.selected_lists ?? []),
      ...publishData?.mail_to,
    ]);
    populateSendFrom(publishData.selected_sender ?? "");
    populateSenderName(publishData.sender_name ?? "");
    populateReplyToInputs(publishData.reply_to_status, publishData.reply_to_email);
    populateIncludeAudience(publishData.include_audience);

    populateScheduleDate(
      publishData.status ?? "",
      publishData.scheduled_datetime ?? "",
      parseInt(publishData.scheduled_timezone ?? "")
    );
    resetLoadedStateOfPublishData();
  }, [publishData]);

  // useEffect(() => {
  //   if (showRecipeintsZeroDialog) {
  //     openOverlay();
  //   } else {
  //     closeOverlay();
  //   }
  // }, [showRecipeintsZeroDialog]);

  const savePublishState = (outlookConnectionState?: boolean) => {
    handleSavePublishState(
      values,
      selectedContactIds,
      selectedListIds,
      sendToObjArray,
      selectedContactEmails,
      outlookConnectionState
    );
  };

  useEffect(() => {
    let unListen: any = null;
    if (unListen) {
      unListen();
    }
    unListen = history.listen(() => {
      handleSavePublishState(values, selectedContactIds, selectedListIds, sendToObjArray, selectedContactEmails);
      unListen();
    });
    return () => {
      if (unListen) {
        unListen();
      }
    };
  }, [values, selectedContactIds, selectedListIds, sendToObjArray]);

  useEffect(() => {
    if (firstTimeLoad && outlookSenderEmailAddress !== "") {
      handleSendFrom(outlookSenderEmailAddress);
    }
    if (firstTimeLoad && userEmail !== "") {
      handleSendFrom(userEmail);
    }
    setFirstTimeLoad(true);
  }, [outlookSenderEmailAddress, userEmail]);

  useEffect(() => {
    if (userName !== "") {
      setSenderName(userName);
      setValues((state) => ({ ...state, senderName: userName }));
    }
    if (outlookSenderName !== "" && outlookRouteReducerState) {
      setSenderName(outlookSenderName);
      setValues((state) => ({ ...state, senderName: outlookSenderName }));
    }
  }, [userName, outlookSenderName]);

  useEffect(() => {
    setValues((state: any) => ({ ...state, senderName: senderName }));
  }, [senderName]);

  useEffect(() => {
    if (recipientLoading) {
      setRecipientCountState("fetching");
    } else {
      setRecipientCountState(total_recipients);
      hideRecipientAsider(true);
    }
  }, [total_recipients, recipientLoading]);

  useEffect(() => {
    setRecipientCountState(
      publishData?.total_recipients === undefined ? "fetching" : publishData?.total_recipients
    );
    if (publishData?.total_recipients > 0 && !recipientCheckBoxState && publishData.include_audience) {
      setRecipientCheckBoxState(true);
    }
  }, [publishData?.total_recipients]);

  useEffect(() => {
    if (scheduleSend === false && publishData.status === "Scheduled" && publishData.is_scheduled) {
      handleSavePublishState(values, selectedContactIds, selectedListIds, sendToObjArray, selectedContactEmails);
    }
  }, [scheduleSend]);

  const todayDateInitialState = () => {
    const today = new Date();
    today.setHours(today.getHours() + 1);
    let timePeriod = today.getHours() < 12 ? "am" : "pm";
    let initialHours = timePeriod === "pm" && today.getHours() > 12 ? today.getHours() - 12 : today.getHours();
    initialHours = timePeriod === "am" && initialHours === 0 ? 12 : initialHours;
    return {
      day: today.getDate().toString().length < 2 ? `0${today.getDate().toString()}` : today.getDate().toString(),
      month: monthsCollection[today.getMonth()],
      year: today.getFullYear().toString(),
      hour: initialHours.toString().length < 2 ? `0${initialHours.toString()}` : initialHours.toString(),
      minute:
        today.getMinutes() < 10
          ? `10`
          : today.getMinutes() < 20
          ? "20"
          : today.getMinutes() < 30
          ? "30"
          : today.getMinutes() < 40
          ? "40"
          : "50",
      period: timePeriod,
    };
  };

  const scheduleReducer = (state: IScheduleState, action: any) => {
    const newState = { ...state };
    switch (action.type) {
      case EmailScheduleActions.UPDATE_DAY:
        newState.day = action.payload;
        return newState;
      case EmailScheduleActions.UPDATE_MONTH:
        newState.month = action.payload;
        return newState;
      case EmailScheduleActions.UPDATE_YEAR:
        newState.year = action.payload;
        // setNumberOfDaysInMonth(newState.month, newState.year);
        return newState;
      case EmailScheduleActions.UPDATE_HOUR:
        newState.hour = action.payload;
        return newState;
      case EmailScheduleActions.UPDATE_MINUTE:
        newState.minute = action.payload;
        return newState;
      case EmailScheduleActions.UPDATE_PERIOD:
        newState.period = action.payload;
        return newState;
      default:
        return newState;
    }
  };
  const [selectedDateState, dispatch] = useReducer(scheduleReducer, todayDateInitialState());

  const isLeapYear = (year: number) => {
    return (0 === year % 4 && 0 !== year % 100) || 0 === year % 400;
  };
  const monthDaysMap: any = {
    Jan: "31",
    Feb: isLeapYear(parseInt(selectedDateState.year)) ? "29 " : "28",
    Mar: "31",
    Apr: "30",
    May: "31",
    Jun: "30",
    Jul: "31",
    Aug: "31",
    Sept: "30",
    Oct: "31",
    Nov: "30",
    Dec: "31",
  };
  const isPastDateTime = () => {
    const today = new Date();
    let hour =
      selectedDateState.period === "am"
        ? selectedDateState.hour
        : (12 + parseInt(selectedDateState.hour)).toString();
    if (hour == "12" && selectedDateState.period === "am") {
      hour = "00";
    } else if (hour == "24") {
      hour = "12";
    }
    if (hour.length == 1) {
      hour = "0" + hour;
    }
    let selectedDateTimeString = `${selectedDateState.year}-${
      monthsMap[selectedDateState.month]
    }-${selectedDateState.day.padStart(2, "0")}`;
    // constructing date time using format YYYY-MM-DDtHH:MM:SS
    selectedDateTimeString += `T${hour}:${selectedDateState.minute.padStart(2, "0")}:${String(
      today.getSeconds()
    ).padStart(2, "0")}`;
    let selectedDate = new Date(selectedDateTimeString);
    let differenceInMinutes = Math.ceil((selectedDate.valueOf() - today.valueOf()) / 1000 / 60);
    if (differenceInMinutes > 0) {
      return false;
    } else {
      return true;
    }
  };

  const yearCollection = [
    new Date().getFullYear().toString(),
    (new Date().getFullYear() + 1).toString(),
    (new Date().getFullYear() + 2).toString(),
  ];
  const dayCollection = new Array(parseInt(monthDaysMap[selectedDateState.month]))
    .fill(1)
    .map((_, i) => i + 1)
    .map((day) => (day < 10 ? `0${day.toString()}` : day.toString()));

  const hoursCollection = new Array(12)
    .fill(1)
    .map((_, i) => i + 1)
    .map((hour) => (hour < 10 ? `0${hour.toString()}` : hour.toString()));

  const minutesCollection = ["00", "10", "20", "30", "40", "50"];

  const formatedText = (state: any) => {
    const day = state.day.length === 1 ? `0${state.day}` : state.day;
    const month = monthsMap[state.month];
    const year = state.year;
    return [month, day, year].join("/");
  };

  const toggleScheduleSend = () => {
    setScheduleSend(!scheduleSend);
    setValues((state) => ({ ...state, isScheduled: false }));
    if (errors.pastScheduleDateCheck) {
      setErrors((state) => ({ ...state, pastScheduleDateCheck: false }));
    }
  };

  const getScheduleTime = () => {
    const date = [selectedDateState.year, monthsMap[selectedDateState.month], selectedDateState.day].join("-");
    let hour =
      selectedDateState.period === "pm" && parseInt(selectedDateState.hour) !== 12
        ? (parseInt(selectedDateState.hour) + 12).toString()
        : selectedDateState.hour;
    hour = selectedDateState.period === "am" && parseInt(selectedDateState.hour) === 12 ? "00" : hour;
    const time = [hour, selectedDateState.minute].join(":");
    const dateTime = [date, time].join(" ");
    return dateTime;
  };

  const yearChangeHandler = (currentValue: any) => {
    dispatch({ type: EmailScheduleActions.UPDATE_YEAR, payload: currentValue });
  };
  const dayChangeHandler = (currentValue: any) => {
    dispatch({ type: EmailScheduleActions.UPDATE_DAY, payload: currentValue });
  };
  const monthChangeHandler = (currentValue: any) => {
    dispatch({ type: EmailScheduleActions.UPDATE_MONTH, payload: currentValue });
  };
  const hourChangeHandler = (currentValue: any) => {
    dispatch({ type: EmailScheduleActions.UPDATE_HOUR, payload: currentValue });
  };
  const minuteChangeHandler = (currentValue: any) => {
    dispatch({ type: EmailScheduleActions.UPDATE_MINUTE, payload: currentValue });
  };
  const amPmChangeHandler = (currentValue: any) => {
    dispatch({ type: EmailScheduleActions.UPDATE_PERIOD, payload: currentValue });
  };

  useEffect(() => {
    store.dispatch({
      type: DESIGN_PREVIEW.LOAD_SUCCESS,
      preview_html: "",
      preview_text: "",
      default_preview_text: "",
    });
    // get sender emails on loading publish
    getPublishData(populateSubjectField);
    getListForAsset(getQueryCampaignId());
    getContactsForAsset("", "", getQueryCampaignId(), 0, 22, "", true, true, true);
    setValues((state) => ({
      ...state,
      campaignId: getQueryCampaignId().toString(),
      emailId: getQueryEmailId().toString(),
    }));
    if (sessionStorage.getItem("callback_success") === "true") {
      sessionStorage.setItem("no_redirect", "true");
      sendEmailWithOutlook(getQueryCampaignId().toString(), getQueryEmailId().toString(), "1", emailType);
      sessionStorage.removeItem("callback_success");
    } else if (sessionStorage.getItem("callback_success") === "false") {
      setShowCallbackError(true);
      sessionStorage.removeItem("callback_success");
    }

    return function cleanUp() {
      store.dispatch({
        type: ASSET_AUDIENCE.RESET,
      });
      store.dispatch({ type: EMAIL_WITH_OUTLOOK.RESET });
      sessionStorage.removeItem("callback_success");
    };
  }, []);

  useEffect(() => {
    if (outlookRoutePublishAPI) {
      if (!isAuthorized) {
        // on connect with outlook if not authorized already
        sessionStorage.setItem("no_redirect", "true");
        sendEmailWithOutlook(
          getQueryCampaignId().toString(),
          getQueryEmailId().toString(),
          "1",
          assetType === "custom" ? "1" : "0"
        );
      }
      setOutlookSuccessInfoMessage(true);
    }
    savePublishState(outlookRoutePublishAPI); // send email configurations call with updated email platform
  }, [outlookRoutePublishAPI]);

  useEffect(() => {
    if (outlookRouteReducerState) {
      if (isAuthorized) {
        setOutlookSuccessInfoMessage(true);
      }
    }
    savePublishState(outlookRouteReducerState); // send email configurations call with updated email platform
  }, [outlookRouteReducerState]);

  useEffect(() => {
    if (firstMount) {
      if (callbackSuccessState === "true") {
        handleSendFrom(outlookSenderEmailAddress);
        handleSendFrom(userEmail);
        setOutlookSuccessInfoMessage(true);
        savePublishState(true);
      } else if (callbackSuccessState === "false") {
        setShowCallbackError(true);
      }
    } else {
      setFirstMount(true);
      getPublishData(populateSubjectField);
    }
  }, [callbackSuccessState]);

  useEffect(() => {
    if (userEmail !== "" || outlookSenderEmailAddress !== "") {
      setScheduleSend(false);
      setValues((state) => ({ ...state, isScheduled: false }));
    }
  }, [userEmail, outlookSenderEmailAddress]);

  const suggestedEmailDropdown: any[] = [];
  suggestedEmails.map((contact: any, index) => {
    let label =
      contact?.contact_name.trim() !== ""
        ? contact?.contact_name.replaceAll(",", " ") +
          (contact?.contact_email !== "" ? ", (" + contact?.contact_email + ")" : "")
        : contact?.contact_email;
    let isDisabled = ["Unsubscribed", "Hard Bounce"].includes(contact?.status);
    suggestedEmailDropdown.push(
      <Option key={index} value={contact?.contact_email} disabled={isDisabled}>
        <Tooltip placement="top" title={label}>
          <Paragraph className={`dropdownValue ${isDisabled ? "disabled" : ""}`} ellipsis={{ rows: 1 }}>
            {label}
          </Paragraph>
        </Tooltip>
        {/* <span>
          {
            contact?.contact_name.trim() !== ""
              ? contact?.contact_name.replaceAll(",", " ") +
              (contact?.contact_email != "" ? ", (" + contact?.contact_email + ")" : "")
              : contact?.contact_email
            // + ", " + contact?.organization
            // to add organization name, uncomment the above line
          }
        </span> */}
        {["Unsubscribed", "Hard Bounce"].includes(contact?.status) ? (
          <span className={contact?.status === "Hard Bounce" ? "unsubText bounceColor" : "unsubText"}>
            {contact?.status}
          </span>
        ) : (
          ""
        )}
      </Option>
    );
  });

  useEffect(() => {
    if (sendToSearchValue.length > 0) {
      searchListContacts(sendToSearchValue);
      suggestedEmails.map((contact: any, index) => {
        suggestedEmailDropdown.push(
          <Option key={index} value={contact?.contact_email}>
            {
              contact?.contact_name.trim() !== ""
                ? contact?.contact_name.replaceAll(",", " ")
                : contact?.contact_email
              // + ", " + contact?.organization
              // to add organization name, uncomment the above line
            }
          </Option>
        );
      });
    }
  }, [sendToSearchValue]);

  const populateEmailPlatform = (emailPlatform: number) => {
    if (emailPlatform !== undefined && emailPlatform !== 0 && emailPlatform !== 3) {
      setValues((state) => ({ ...state, sendWith: emailPlatform.toString() }));
    } else {
      setValues((state) => ({ ...state, sendWith: "1" }));
    }
  };
  const populateSenderName = (name: string) => {
    if (name) {
      setSenderName(name);
      setValues((state) => ({ ...state, senderName: name }));
    }
  };

  const populateSubjectField = (subject: string) => {
    if (subject) {
      setSubjectRef(subject);
      setValues((state) => ({ ...state, emailSubject: subject }));
    }
  };
  const populateIncludeAudience = (includeAudience: boolean) => {
    if (includeAudience !== undefined) {
      setRecipientCheckBoxState(includeAudience);
      setValues((state) => ({ ...state, includeAudience: includeAudience }));
    }
  };

  const populateReplyToInputs = (replyToSame: boolean, replyToEmail: string) => {
    if (replyToSame !== undefined && replyToEmail) {
      setValues((state) => ({ ...state, sameReplyTo: replyToSame, replyTo: replyToEmail }));
      setReplyToEmailCheck(replyToSame);
    }
  };

  const populateScheduleDate = (status: string, dateTime: string, tzHourOffset: number) => {
    if (status !== "Scheduled") {
      return;
    }
    setScheduleSend(true);
    const [date, time] = dateTime.split(" ");
    const [hour, minute] = time.split(":");
    const [year, month, day] = date.split("-");
    let dateObject = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour) + tzHourOffset,
      parseInt(minute)
    );

    yearChangeHandler(dateObject.getFullYear().toString());

    dayChangeHandler(
      dateObject.getDate().toString().length > 1
        ? dateObject.getDate().toString()
        : "0" + dateObject.getDate().toString()
    );

    monthChangeHandler(monthsCollection[dateObject.getMonth()]);

    const hoursInNumber =
      dateObject.getHours() > 12
        ? dateObject.getHours() - 12
        : dateObject.getHours() === 0
        ? 12
        : dateObject.getHours();

    hourChangeHandler(
      hoursInNumber.toString().length > 1 ? hoursInNumber.toString() : "0" + hoursInNumber.toString()
    );

    minuteChangeHandler(
      dateObject.getMinutes().toString().length > 1
        ? dateObject.getMinutes().toString()
        : "0" + dateObject.getMinutes().toString()
    );

    amPmChangeHandler(dateObject.getHours() >= 12 ? "pm" : "am");
    // after first time scheduled time is populated we store the value for comparison
    setScheduledTime(getScheduleTime());
  };

  const populateContactAndListIds = (savedSendToArray: any[]) => {
    const sendToNameArray: string[] = [];
    const sendToContactEmailArray: string[] = [];
    const sendToContactNameArray: string[] = [];
    const sendToListNameArray: string[] = [];
    const sendToIdArray: number[] = [];
    const selectedContactIdArray: number[] = [];
    const selectedListIdArray: number[] = [];
    const sendToObjectsArray: any[] = [];
    savedSendToArray.map((item: any) => {
      if (item.contact_id) {
        sendToContactNameArray.push(item.contact_name?.replaceAll(",", " ") || item.contact_email);
        sendToContactEmailArray.push(item.contact_email);
        // sendToIdArray.push(item.contact_id);
        selectedContactIdArray.push(item.contact_id);
        sendToObjectsArray.push({
          value: item.contact_email,
          label: item.contact_name?.replaceAll(",", " ") || item.contact_email,
        });
      } else if (item.list_id) {
        sendToListNameArray.push(item.list_name);
        sendToIdArray.push(item.list_id);
        sendToNameArray.push(item.list_name);
        selectedListIdArray.push(item.list_id);
        sendToObjectsArray.push({ value: item.list_id, label: item.list_name });
      } else {
        sendToObjectsArray.push({ value: item, label: item });
        sendToNameArray.push(item);
        // if (values.sendTo === "") setValues((state) => ({ ...state, sendTo: item }));
        // if (values.sendTo !== "") setValues((state) => ({ ...state, sendTo: state.sendTo + "," + item }));
      }
    });
    setSelectedContactIds([...selectedContactIdArray]);
    setSelectedContactNames([...sendToContactNameArray]);
    setSelectedContactEmails([...sendToContactEmailArray]);
    setSelectedListIds([...selectedListIdArray]);
    setSelectedListNames([...sendToListNameArray]);
    setSendToObjectArray([...sendToObjectsArray]);
  };

  const populateSendFrom = (sendFrom: string) => {
    if (sendFrom === "") {
      return;
    }
    setValues((state) => ({ ...state, sendFrom: sendFrom }));
  };

  // handle isTest Switch
  const handleTestSwitch = (value: boolean) => {
    setValues((state) => ({ ...state, isTestEmail: value }));
    setErrors({ ...errors, isSendToFieldEmpty: false });
    if (value) {
      setRecipientCheckBoxState(false);
      hideRecipientAsider(true);
    } else setRecipientCheckBoxState(true);
  };

  // handle isTest Switch
  const handleWebinarSwitch = (value: boolean) => {
    setValues((state) => ({ ...state, isXiqEvent: value }));
    setErrors({ ...errors, isSendToFieldEmpty: false });
  };

  const handleRecipientCheckBox = () => {
    setRecipientCheckBoxState(!recipientCheckBoxState);
    setValues((state) => ({ ...state, includeAudience: !recipientCheckBoxState }));
    setErrors({ ...errors, isSendToFieldEmpty: false });
    handleSavePublishState(
      { ...values, includeAudience: !recipientCheckBoxState },
      selectedContactIds,
      selectedListIds,
      sendToObjArray,
      selectedContactEmails
    );
  };

  // handle radio buttons
  const handleChangeRadio = (e: any) => {
    setValues((state) => ({ ...state, [e.target.name]: e.target.value }));
    if (errors.sendWith && e.target.value) {
      setErrors({ ...errors, sendWith: false });
    }
  };

  // handle send from Dropdown
  const handleSendFrom = (value: any) => {
    setValues((state) => ({ ...state, sendFrom: value }));
    if (errors.sendFrom && value) {
      setErrors({ ...errors, sendFrom: false });
    }
  };

  // modified code
  const handleSendToUpdated = (obj: any, id?: any, fromRecommended?: boolean) => {
    setOpenStateDropdown(false);
    let { value, label } = obj;
    let index = sendToObjArray.findIndex((item: any) => item.value === value);
    if (index >= 0) {
    } else {
      if (!fromRecommended) {
        let updatedContactIds = [...selectedContactIds];
        let updatedContactNames = [...selectedContactEmails];
        let updatedContactEmails = [...selectedContactEmails];
        if (!updatedContactEmails.includes(value)) {
          let contact = suggestedEmails.find((item) => item.contact_email === value);
          if (contact) {
            if (contact.status === "Subscribed") {
              updatedContactIds.push(contact.contact_id);
              updatedContactNames.push(contact.contact_name.trim() || contact.contact_email);
              updatedContactEmails.push(contact.contact_email);
              setSelectedContactIds([...updatedContactIds]);
              setSelectedContactNames([...updatedContactNames]);
              setSelectedContactEmails([...updatedContactEmails]);
              setSendToObjectArray([
                ...sendToObjArray,
                { value, label: contact.contact_name.trim() || contact.contact_email },
              ]);
              // handling save
              handleSavePublishState(
                values,
                updatedContactIds,
                selectedListIds,
                [...sendToObjArray, { value, label: contact.contact_name.trim() || contact.contact_email }],
                updatedContactEmails
              );
            }
          } else {
            contact = campaignAudienceContacts.find((contact: any) => contact.email === value);
            if (contact) {
              if (contact?.status === "Subscribed") {
                setSendToObjectArray([...sendToObjArray, { value, label }]);
                handleSavePublishState(
                  values,
                  selectedContactIds,
                  selectedListIds,
                  [...sendToObjArray, { value, label }],
                  selectedContactEmails
                );
              }
            } else {
              setSendToObjectArray([...sendToObjArray, { value, label }]);
              handleSavePublishState(
                values,
                selectedContactIds,
                selectedListIds,
                [...sendToObjArray, { value, label }],
                selectedContactEmails
              );
            }
          }
        } else {
          setSendToObjectArray([...sendToObjArray, { value, label }]);
          handleSavePublishState(
            values,
            selectedContactIds,
            selectedListIds,
            [...sendToObjArray, { value, label }],
            selectedContactEmails
          );
        }
      } else {
        setSendToObjectArray([...sendToObjArray, { value, label }]);
      }
    }
    setSendToSearchValue("");
  };

  // modified code
  const handleSendToMultiple = (arr: any) => {
    let updatedSendToObject = [...sendToObjArray];
    let emailsArray = updatedSendToObject.map((item) => item.value);
    let updatedContactIds = [...selectedContactIds];
    let updatedContactNames = [...selectedContactNames];
    let updatedContactEmails = [...selectedContactEmails];
    arr.forEach((item: any) => {
      let { value, label } = item;
      let index = updatedSendToObject.findIndex((item) => item.value === value);
      if (index >= 0) {
      } else {
        let contact = campaignAudienceContacts.find((contact: any) => contact.email === item.value);

        if (!emailsArray.includes(value)) {
          if (!updatedContactEmails.includes(value)) {
            let recommended = recommendedPeople.find((element) => element.email === value);
            if (recommended) {
              updatedContactIds.push(recommended.id);
              updatedContactNames.push(recommended.name || recommended.email);
              updatedContactEmails.push(recommended.email);
              updatedSendToObject.push({ label: recommended.name || recommended.email, value });
              emailsArray.push(value);
            } else {
              if (contact) {
                if (contact?.status === "Subscribed") {
                  updatedSendToObject.push(item);
                  emailsArray.push(value);
                }
              } else {
                updatedSendToObject.push(item);
                emailsArray.push(value);
              }
            }
          }
        }
      }
    });
    setSendToObjectArray([...updatedSendToObject]);
    setSelectedContactIds([...updatedContactIds]);
    setSelectedContactNames([...updatedContactNames]);
    setSelectedContactEmails([...updatedContactEmails]);
    handleSavePublishState(
      values,
      [...updatedContactIds],
      selectedListIds,
      [...updatedSendToObject],
      [...updatedContactEmails]
    );
  };

  const handleReplyTo = (value: any) => {
    setValues((state) => ({ ...state, replyTo: value }));
    if (errors.replyTo && value) {
      setErrors({ ...errors, replyTo: false });
    }
  };

  // handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === "emailSubject") {
      setSubjectRef(e.target.value);
      // updateEmailNameForBrowserEmailList(getQueryEmailId(), e.target.value);
    }
    setValues((state) => ({ ...state, [e.target.name]: e.target.value }));
    // logic for removing error div once a user retype anything in the input fields
    Object.entries(errors).forEach(([key, value]) => {
      if (key === e.target.name && value) {
        setErrors({ ...errors, [key]: false });
      }
    });
  };

  const handleSenderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((state) => ({ ...state, senderName: e.target.value }));
    setSenderName(e.target.value);
    // logic for removing error div once a user retype anything in the input fields
    Object.entries(errors).forEach(([key, value]) => {
      if (key === e.target.name && value) {
        setErrors({ ...errors, [key]: false });
      }
    });
  };

  // handle checkboxes
  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((state) => ({ ...state, [e.target.name]: e.target.checked }));
    if (e.target.checked) setReplyToEmailCheck(true);
  };

  const redirectAfterSend = () => {
    history.push("/campaigns/content/sent?campaign_id=" + getQueryCampaignId());
  };

  const checkFieldValidation = (values: any, stopScroll?: boolean) => {
    let errorsObject: any = {};

    let { emailSubject, senderName, sendFrom, replyTo, sameReplyTo, isTestEmail, sendWith } = values;

    // send to field errors
    let sendTo = sendToObjArray.map((item: any) => item.value).join(",");
    if (
      sendTo.length === 0 &&
      selectedContactIds.length === 0 &&
      selectedListIds.length === 0 &&
      (values.isTestEmail || !recipientCheckBoxState)
    ) {
      errorsObject.isSendToFieldEmpty = true;
      if (!stopScroll) document.getElementById("sendToDivId")?.scrollIntoView({ behavior: "smooth" });
    }
    if (
      sendTo.length === 0 &&
      selectedContactIds.length === 0 &&
      selectedListIds.length === 0 &&
      (!values.isTestEmail || !recipientCheckBoxState) &&
      recipientCountState === 0
    ) {
      errorsObject.isSendToFieldEmpty = true;
      if (!stopScroll) document.getElementById("sendToDivId")?.scrollIntoView({ behavior: "smooth" });
    }

    // reply to field errors
    if (!sameReplyTo) {
      if (replyTo?.length === 0) {
        errorsObject.replyTo = true;
        if (!stopScroll) document.getElementById("replyToDivId")?.scrollIntoView({ behavior: "smooth" });
      } else {
        if (validateEmail(values.replyTo)) {
          errorsObject.replyTo = false;
        } else {
          errorsObject.replyTo = true;
          if (!stopScroll) document.getElementById("replyToDivId")?.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
    if (sameReplyTo && !validateEmail(values.sendFrom)) {
      errorsObject.replyTo = true;
      if (!stopScroll) document.getElementById("replyToDivId")?.scrollIntoView({ behavior: "smooth" });
    }

    // send from field errors
    if (sendFrom?.length === "Select Email") {
      errorsObject.sendFrom = true;
      if (!stopScroll) document.getElementById("SendFromDivId")?.scrollIntoView({ behavior: "smooth" });
    }
    if (!validateEmail(values.sendFrom)) {
      errorsObject.sendFrom = true;
      if (!stopScroll) document.getElementById("SendFromDivId")?.scrollIntoView({ behavior: "smooth" });
    }

    // email subject field error
    if (!emailSubject || emailSubject?.trim().length === 0) {
      errorsObject.emailSubject = true;
      if (!stopScroll) document.getElementById("emailSubjectDivId")?.scrollIntoView({ behavior: "smooth" });
    }

    // sender name field error
    if (!senderName || senderName?.trim().length === 0) {
      errorsObject.senderName = true;
      if (!stopScroll) document.getElementById("senderNameDivId")?.scrollIntoView({ behavior: "smooth" });
    }

    if (sendWith === "" && publishData.xiq_internal_user && !outlookRouteReducerState) {
      errorsObject.sendWith = true;
    } else if (sendWith === "" && !publishData.xiq_internal_user) {
      sendWith = "0";
    } else if (outlookRouteReducerState) {
      sendWith = "0";
    }
    if (scheduleSend && isPastDateTime()) {
      errorsObject.pastScheduleDateCheck = true;
    } else {
      errorsObject.pastScheduleDateCheck = false;
    }
    return errorsObject;
  };

  const isFormValid = (values: any) => {
    const errorsObject = checkFieldValidation(values);

    setErrors((state) => ({ ...state, ...errorsObject }));
    const doesNotHasErrors = Object.values(errorsObject).every((item) => item === false);

    if (!doesNotHasErrors) {
      setSendDisabled(false);
      return false;
    }
    return true;
  };

  const preparePublishData = () => {
    let { emailSubject, sendFrom, replyTo, sameReplyTo, isTestEmail, sendWith, isScheduled, isXiqEvent } = values;

    let formData = new FormData();
    formData.append("email_id", values.emailId);
    formData.append("sender_name", senderName);
    formData.append("sender_email", sendFrom);
    formData.append("subject", emailSubject);
    // formData.append("emails_to", sendTo);
    formData.append("campaign_id", values.campaignId);
    formData.append("include_audience", values.includeAudience ? "1" : "0");
    formData.append("reply_to_status", sameReplyTo ? "1" : "0");
    if (outlookRouteReducerState) {
      formData.append("send_email_platform", "3");
    } else {
      if (publishData.xiq_internal_user)
        formData.append("send_email_platform", outlookRouteReducerState ? "3" : sendWith.toString());
    }
    formData.append("reply_to", sameReplyTo ? sendFrom : replyTo);
    formData.append("sender_domain", sendFrom);
    formData.append("test_email", isTestEmail ? "1" : "0");
    if (isXiqEvent) {
      formData.append("xiq_event", "1");
    }
    formData.append("is_scheduled", isScheduled ? "1" : "0");

    let sendToObjectsArray = [...sendToObjArray];
    let contactEmails = [...selectedContactEmails];

    if (scheduleSend) {
      formData.append("time", getScheduleTime());
    }

    formData.append("outlook_route", outlookRouteReducerState ? "1" : "0");
    if (selectedContactIds.length === 0) {
      formData.append("contact_ids", "");
    } else {
      for (const item of selectedContactIds) {
        formData.append("contact_ids", item.toString());
      }
      contactEmails.forEach((email) => {
        sendToObjectsArray = sendToObjectsArray.filter((item) => item.value !== email);
      });
    }

    if (selectedListIds.length === 0) {
      formData.append("list_ids", "");
    } else {
      for (const listId of selectedListIds) {
        formData.append("list_ids", listId.toString());
        sendToObjectsArray = sendToObjectsArray.filter((item) => item.value !== listId);
      }
    }
    if (sendToObjectsArray.length > 0) {
      let sendToArray = sendToObjectsArray.map((item) => item.value);
      let emailsTo = sendToArray.join(",");
      formData.append("emails_to", emailsTo);
    } else {
      formData.append("emails_to", "");
    }
    return [formData, isPremium, setSendDisabled];
  };

  const prepareDataForStorage = (
    values: any,
    errors: any,
    contactIds: any[],
    listIds: any[],
    sendToObjects: any[],
    contactEmails: any[],
    outlookConnectionState?: boolean
  ) => {
    let { emailSubject, sendFrom, replyTo, sameReplyTo, sendWith } = values;
    let formData = new FormData();
    formData.append("campaign_id", values.campaignId);
    formData.append("email_id", values.emailId);
    formData.append("outlook_route", outlookConnectionState ? "1" : "0");
    formData.append("is_scheduled", scheduleSend ? "1" : "0");
    formData.append("reply_to_status", sameReplyTo ? "1" : "0");
    formData.append("include_audience", values.includeAudience ? "1" : "0");

    let sendToObjectsArray = [...sendToObjects];

    if (contactIds.length === 0) {
      formData.append("contact_ids", "");
    } else {
      for (const item of contactIds) {
        formData.append("contact_ids", item.toString());
      }

      contactEmails.forEach((email) => {
        sendToObjectsArray = sendToObjectsArray.filter((item) => item.value !== email);
      });
    }

    if (listIds.length === 0) {
      formData.append("list_ids", "");
    } else {
      for (const listId of listIds) {
        formData.append("list_ids", listId.toString());
        sendToObjectsArray = sendToObjectsArray.filter((item) => item.value !== listId);
      }
    }

    if (!errors.emailSubject) {
      formData.append("subject", emailSubject);
    }
    if (!errors.senderName) {
      formData.append("sender_name", senderName);
    }
    if (!errors.sendFrom && !outlookConnectionState) {
      formData.append("sender_email", sendFrom);
      formData.append("sender_domain", sendFrom);
    } else if (!outlookConnectionState) {
      formData.append("sender_email", "");
      formData.append("sender_domain", "");
    }
    if (!errors.replyTo) {
      formData.append("reply_to_email", sameReplyTo ? sendFrom : replyTo);
    } else if (!outlookConnectionState) {
      formData.append("reply_to_email", "");
    }

    if (sendToObjectsArray.length > 0) {
      let sendToArray = sendToObjectsArray.map((item) => item.value);
      let emailsTo = sendToArray.join(",");
      formData.append("emails_to", emailsTo);
    } else {
      formData.append("emails_to", "");
    }

    if (!errors.sendWith && publishData.xiq_internal_user) {
      formData.append(
        "send_email_platform",
        outlookConnectionState || outlookRouteReducerState ? "3" : sendWith.toString()
      );
    }

    return formData;
  };

  const sendMailConfirmHandler = () => {
    setShowConfirmDialog(false);
    const [formData, isPremium, setSendDisabled]: any = preparePublishData();
    scheduleSend
      ? scheduleEmail(formData, isPremium, setSendDisabled)
      : sendPublishData(formData, isPremium, setSendDisabled);
  };

  const handlePublishSubmit = () => {
    if (!isFormValid(values)) {
      return;
    }

    // if workflow is not published it will not be able to publish email
    if (workflowCurrentState === "draft" || workflowEmailStatus === "wf_graph") {
      setShowDraftWorkflowPopup(true);
      return;
    }
    //* TODO: Refactor Code: Remove this line and uncomment the above - done

    const formData = new FormData();
    formData.append("campaign_id", getQueryCampaignId().toString());
    formData.append("test_email", values.isTestEmail ? "1" : "0");
    formData.append("include_audience", recipientCheckBoxState ? "1" : "0");
    formData.append("contact_ids", selectedContactIds.join(","));
    formData.append("list_ids", selectedListIds.join(","));
    if (sendToObjArray.length > 0) {
      let emails_to: any[] = [];
      sendToObjArray.filter(function (item: any) {
        if (item.value === item.label) emails_to.push(item.value);
        // if (validateEmail(item.value.toString())) emails_to.push(item.value);
      });
      formData.append("to_emails", emails_to.join(","));
    } else {
      formData.append("to_emails", "");
    }
    getRecipientCount(formData, showCountPopup);
    setSendDisabled(true);
  };
  const showCountPopup = (count: any) => {
    setMailToCount(count);
    if (count === 0) {
      setShowRecipientsZeroDialog(true);
    } else {
      setShowConfirmDialog(true);
    }
  };
  const handleSavePublishState = (
    values: any,
    selectedContactIdsArgs: any[],
    selectedListIdsArgs: any[],
    sendToObjectsArrayArgs: any[],
    selectedContactEmailsArgs: any[],
    outlookConnectionState?: boolean
  ) => {
    const errorFields = { ...errors, ...checkFieldValidation(values, true) };
    const allFieldsHaveError = Object.values(errorFields).every((item) => item === true);

    if (values.campaignId === "" && values.emailId === "") {
      return;
    }
    if (allFieldsHaveError) {
      dispatchErrorInSaveState();
      return;
    }

    const formData: any = prepareDataForStorage(
      values,
      errorFields,
      selectedContactIdsArgs,
      selectedListIdsArgs,
      sendToObjectsArrayArgs,
      selectedContactEmailsArgs,
      outlookConnectionState
    );
    saveEmailData(formData, values.isScheduled, editionStatus);
  };

  function handleOnClickBrowsePeople() {
    if (recommendedPeopleLength === 0) {
      getRecommendedPeople();
    }
    if (recommendedListLength === 0) {
      getRecommendedLists();
    }
    setShowRecipientAsider(false);
    setShowAudienceAsider(true);
  }

  function handleAddPeople(id: number, name: string, audience_type: string, email?: string) {
    if (audience_type === "contact") {
      if (name === "" && email !== undefined) {
        name = email;
      }
      if (selectedContactIds.indexOf(id) < 0) {
        setSelectedContactIds([...selectedContactIds, id]);
        setSelectedContactNames([...selectedContactNames, name]);
        // check if email is provided otherwise find it and append it
        if (email) {
          setSelectedContactEmails([...selectedContactEmails, email]);
          handleSavePublishState(values, [...selectedContactIds, id], selectedListIds, sendToObjArray, [
            ...selectedContactEmails,
            email,
          ]);
        } else {
          let contact = recommendedPeople.find((item) => item.id.toString() === id.toString());
          if (contact) {
            setSelectedContactEmails([...selectedContactEmails, contact.email]);
            handleSavePublishState(values, [...selectedContactIds, id], selectedListIds, sendToObjArray, [
              ...selectedContactEmails,
              contact.email,
            ]);
          } else {
            setSelectedContactEmails([...selectedContactEmails, ""]);
            handleSavePublishState(values, [...selectedContactIds, id], selectedListIds, sendToObjArray, [
              ...selectedContactEmails,
              "",
            ]);
          }
        }
        // handleSendTo(name, id, true);
        handleSendToUpdated({ value: email, label: name }, id, true);
        if (errors.isSendToFieldEmpty) setErrors({ ...errors, isSendToFieldEmpty: false });
      } else {
        handleRemovePeopleUpdated(id, name, audience_type, email);
      }
    } else if (audience_type === "list") {
      if (selectedListIds.indexOf(id) < 0) {
        setSelectedListIds([...selectedListIds, id]);
        setSelectedListNames([...selectedListNames, name]);
        handleSavePublishState(
          values,
          selectedContactIds,
          [...selectedListIds, id],
          sendToObjArray,
          selectedContactEmails
        );
        // handleSendTo(name, id, true);
        handleSendToUpdated({ value: id, label: name }, id, true);
        if (errors.isSendToFieldEmpty) setErrors({ ...errors, isSendToFieldEmpty: false });
      } else {
        handleRemovePeopleUpdated(id, name, audience_type);
      }
    }
  }

  const handleOnClear = () => {
    setSelectedContactIds([]);
    setSelectedContactNames([]);
    setSelectedContactEmails([]);
    setSelectedListIds([]);
    setSelectedListNames([]);
    setSendToObjectArray([]);
    setErrors({ ...errors, enterValidEmail: false });
    setValues((state) => ({ ...state, sendTo: "" }));
    handleSavePublishState(values, [], [], [], []);
  };

  // modified code
  const removeFromSendToUpdated = (obj: any, fromRecommended?: boolean, audience_type?: string) => {
    let { value, id } = obj;
    let sendToObjectsArray = [...sendToObjArray];
    let index = sendToObjectsArray.findIndex((item) => item.value === value);
    if (index >= 0) {
      sendToObjectsArray.splice(index, 1);
      setSendToObjectArray([...sendToObjectsArray]);
    }

    if (!fromRecommended) {
      if (typeof value === "number") {
        let updatedListIds = [...selectedListIds];
        let updatedListNames = [...selectedListNames];
        let listIndex = updatedListIds.findIndex((item) => item === value);
        if (listIndex >= 0) {
          updatedListIds.splice(listIndex, 1);
          updatedListNames.splice(listIndex, 1);
          setSelectedListIds([...updatedListIds]);
          setSelectedListNames([...updatedListNames]);
          handleSavePublishState(
            values,
            selectedContactIds,
            updatedListIds,
            [...sendToObjectsArray],
            selectedContactEmails
          );
        }
      } else {
        let updatedContactIds = [...selectedContactIds];
        let updatedContactNames = [...selectedContactEmails];
        let updatedContactEmails = [...selectedContactEmails];
        let contactIndex = updatedContactEmails.findIndex((item) => item === value);
        if (contactIndex >= 0) {
          updatedContactIds.splice(contactIndex, 1);
          updatedContactNames.splice(contactIndex, 1);
          updatedContactEmails.splice(contactIndex, 1);
          setSelectedContactIds([...updatedContactIds]);
          setSelectedContactNames([...updatedContactNames]);
          setSelectedContactEmails([...updatedContactEmails]);
          handleSavePublishState(
            values,
            updatedContactIds,
            selectedListIds,
            [...sendToObjectsArray],
            updatedContactEmails
          );
        } else {
          handleSavePublishState(
            values,
            updatedContactIds,
            selectedListIds,
            [...sendToObjectsArray],
            updatedContactEmails
          );
        }
      }
    } else {
      if (audience_type === "contact") {
        const updatedContactIds = [...selectedContactIds];
        const updatedContactNames = [...selectedContactNames];
        const updatedContactEmails = [...selectedContactEmails];
        let index = updatedContactIds.indexOf(id);
        if (index >= 0) {
          updatedContactIds.splice(index, 1);
          updatedContactNames.splice(index, 1);
          setSelectedContactIds(updatedContactIds);
          setSelectedContactNames(updatedContactNames);
          setSelectedContactEmails(updatedContactEmails);
          handleSavePublishState(
            values,
            updatedContactIds,
            selectedListIds,
            [...sendToObjectsArray],
            updatedContactEmails
          );
        }
      } else if (audience_type === "list") {
        const updatedListIds = [...selectedListIds];
        const updatedListNames = [...selectedListNames];
        let index = updatedListIds.indexOf(id);
        if (index >= 0) {
          updatedListIds.splice(index, 1);
          updatedListNames.splice(index, 1);
          setSelectedListIds(updatedListIds);
          setSelectedListNames(updatedListNames);
          handleSavePublishState(
            values,
            selectedContactIds,
            updatedListIds,
            [...sendToObjectsArray],
            selectedContactEmails
          );
        }
      }
    }
  };

  // modified code
  function handleRemovePeopleUpdated(id: number, name: string, audience_type: string, email?: string) {
    removeFromSendToUpdated({ value: email ?? id, label: name, id }, true, audience_type);
  }
  const renderToList = (value: any) => {
    history.push(
      `/audiences/lists/view?list_id=${value}&from=publish&campaign_id=${getQueryCampaignId()}&email_id=${getQueryEmailId()}`
    );
  };

  function tagRender(props: any) {
    const { label, value, closable, onClose } = props;
    let isList = selectedListNames.includes(label);
    const onPreventMouseDown = (event: any) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        color={value}
        onMouseDown={onPreventMouseDown}
        className={isList ? "list-tag" : ""}
        style={{
          marginRight: 3,
          borderRadius: 15,
          backgroundColor: isList ? "#E7EDFF" : "#E9FFF0",
          color: isList ? "#001760" : "#1EC252",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          padding: "3px 5px",
          margin: "2px 1px",
          cursor: isList ? "pointer" : "",
        }}
      >
        <span className={isList ? "list-item" : ""}>
          {
            <span onClick={() => isList && renderToList(value)} className="edit-option">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10.9388 0.867106C11.4934 0.309992 12.248 -0.00232947 13.0342 1.071e-05L13.0346 0.000287207C13.8206 0.000287207 14.5747 0.312591 15.1307 0.868304C15.6867 1.42416 15.9994 2.17807 15.9997 2.96434C15.9999 3.7506 15.688 4.5047 15.1325 5.06092L9.7544 10.4337C8.83762 11.3564 7.58966 11.8742 6.2888 11.8711H4.12895V9.71115C4.12517 8.40931 4.64284 7.16018 5.56625 6.24267L10.9388 0.867106ZM8.66288 9.33944L14.0354 3.96388L14.0382 3.96656C14.3769 3.60527 14.5024 3.09315 14.369 2.6161C14.2358 2.13905 13.8631 1.76645 13.386 1.63306C12.909 1.49969 12.3971 1.62514 12.0358 1.96392L6.66049 7.33949C6.02921 7.967 5.67533 8.82101 5.67731 9.71115V10.3227H6.2888C7.17982 10.3254 8.03466 9.97128 8.66288 9.33944Z"
                  fill="white"
                />
                <path
                  d="M2.83867 16H12.1286C12.8813 16 13.6033 15.7008 14.1356 15.1685C14.6681 14.636 14.9671 13.914 14.9671 13.1612V9.03207H13.4188V13.1612C13.4188 13.5034 13.2827 13.8316 13.0408 14.0737C12.7989 14.3156 12.4708 14.4515 12.1286 14.4515H2.83867C2.49648 14.4515 2.16833 14.3156 1.92622 14.0737C1.6843 13.8316 1.5484 13.5034 1.5484 13.1612V3.87088C1.5484 3.52868 1.6843 3.20051 1.92622 2.95858C2.16833 2.71664 2.49647 2.58056 2.83867 2.58056H6.9676V1.03226H2.83867C2.08589 1.03226 1.3639 1.33126 0.831436 1.86374C0.299157 2.39604 0 3.11807 0 3.87088V13.1612C0 13.914 0.299166 14.636 0.831436 15.1685C1.3639 15.7008 2.08589 16 2.83867 16L2.83867 16Z"
                  fill="white"
                />
              </svg>
              Edit List
            </span>
          }
          <span className={`icon ${!isList ? "contact-icon" : ""}`}>
            {isList ? <ListIconOrange /> : <Group />}
          </span>
          {label}
          <span
            onClick={() => {
              removeFromSendToUpdated({ value, label });
            }}
            className={`icon-cross ${!isList ? "success-cross" : ""}`}
          >
            <RemoveCross />
          </span>
        </span>
      </Tag>
    );
  }

  // This check for if a scheduled email time is changed
  const checkScheduleUpdate = () => {
    return editionStatus === "Scheduled" && scheduledTime !== getScheduleTime().toString();
  };

  // modified code
  const handleMultipleInputs = (str: string) => {
    const valueArray: string[] = str.split(",");
    const uniqueSendToNameArrayAll = [...new Set(valueArray)];
    const uniqueSendToNameArray = uniqueSendToNameArrayAll.filter((item) => item.length > 0);
    if (!uniqueSendToNameArray.every((item) => validateEmail(item))) {
      setErrors({ ...errors, enterValidEmail: true });
      return;
    }
    if (uniqueSendToNameArray.length > 1) {
      handleSendToMultiple(uniqueSendToNameArray.map((item) => ({ value: item, label: item })));
    } else if (uniqueSendToNameArray.length === 1) {
      handleSendToUpdated({ value: uniqueSendToNameArray[0], label: uniqueSendToNameArray[0] });
    }
    setSendToSearchValue("");
  };

  function handleKeyDown(e: any) {
    setOpenStateDropdown(true);
    let value = e.target.value;
    if (e.key.toLowerCase() === "enter") {
      if (value.startsWith(",") || value.endsWith(",")) {
        handleMultipleInputs(value);
      }
      if (value.includes(",")) {
        handleMultipleInputs(value);
        setSendToSearchValue("");
      } else {
        if (validateEmail(value)) {
          handleSendToUpdated({ value, label: value });
          setSendToSearchValue("");
        }
      }
    } else if (value.startsWith(",")) {
      setSendToSearchValue(value.replace(/,/g, ""));
    } else if (value.endsWith(",") || value.includes(",") || e.key === ",") {
      handleMultipleInputs(value);
      setSendToSearchValue("");
    }
  }

  function handleSendToSearchValue(e: any) {
    if (e.startsWith(",")) {
      setSendToSearchValue(e.replace(",", ""));
    } else {
      setSendToSearchValue(e);
    }
  }

  const emailWithOutlookConfirmationHandler = (actionType: string) => {
    if (actionType === "onConfirm") {
      resetOutlookConnection(); // reset outlook connection Reducer State and trigger the respective useEffect
      const name = first_name + " " + last_name;
      populateSenderName(name);
      populateSendFrom("Select Email");
      setOutlookSuccessInfoMessage(false);
    } else if (actionType === "onCancel") {
    }
    setEmailWithOutlookConfirmation(false);
  };

  const recipientsApplyClickHandler = (
    updatedListsState: any[],
    updatedContactsState: any[],
    updatedListObject: any[],
    updatedContactObject: any[]
  ) => {
    updateCampaignRecipientState(
      getQueryCampaignId().toString(),
      updatedListsState.length > 0 ? updatedListsState : campaignAudienceLists,
      updatedContactsState.length > 0 ? updatedContactsState : campaignAudienceContacts,
      updatedListObject,
      updatedContactObject
    );
    // setShowAudienceAsider(false);
    // hideRecipientAsider(false);
  };

  useEffect(() => {
    if (outlookConnectionError) {
      setShowCallbackError(true);
      store.dispatch({
        type: EMAIL_WITH_OUTLOOK.RESET_ERROR,
      });
      const name = first_name + " " + last_name;
      populateSenderName(name);
      populateSendFrom("Select Email");
    }
  }, [outlookConnectionError]);

  useEffect(() => {
    if (exportPromoCodesSuccess) {
      setShowExportSuccessPopup(true);
      setStickyHeaderBackOverlay(true);
      setMainNavBackOverlay(true);
      store.dispatch({
        type: PROMO_CODES.EXPORT_SUCCESS,
        promoCodesExportSuccess: false,
      });
    }
  }, [exportPromoCodesSuccess]);

  const importListFunc = () => {
    setShowImportListAsider(true);
    setStickyHeaderBackOverlay(true);
    setMainNavBackOverlay(true);
    if (window.location.pathname.startsWith("/campaigns")) removeGillRoyIcon();
  };

  const handleConfirmExportSalesXPopup = () => {
    setShowExportSuccessPopup(false);
    closeOverlay();
    setStickyHeaderBackOverlay(false);
    setMainNavBackOverlay(false);
  };

  const sendEmailComfirmHandler = () => {
    if (outlookRouteReducerState && mailToCount > outlookRemainingEmails) {
      setShowConfirmDialog(false);
      setSendDisabled(false);
    } else {
      sendMailConfirmHandler();
    }
    store.dispatch({
      type: EMAIL_LIST.DRAFT_UPDATE,
      payload: emailIdOfDraftEmail,
    });
  };

  return (
    <>
      <FloatingSidebarTwo
        inPublish={true}
        isToggle={true}
        keywordAsider={true}
        isText={false}
        isToggleAsider={showAudienceAsider}
        onClickAudienceDone={() => {
          setShowAudienceAsider(false);
        }}
        handleAddPeople={handleAddPeople}
        selectedContactIds={selectedContactIds}
        selectedListIds={selectedListIds}
        page="publish"
        showRecipientAsider={showRecipientAsider}
        hideRecipientAsider={hideRecipientAsider}
        recipientsApplyClickHandler={recipientsApplyClickHandler}
      >
        {publishDataLoading === false && (
          <Wrapper>
            <div className="header-section" style={{ marginTop: "100px" }}>
              {openPublishedConfirmation && (
                <ConfirmDialog
                  icon={SendingWhite}
                  title={scheduleSend ? "" : `Sending “${values?.emailSubject}” email`}
                  titleQuestion=""
                  after={true} // for adding after class for showing shadow
                  descriptions={
                    scheduleSend
                      ? "Email has been Scheduled to be sent later."
                      : "We’re sending your email to your recipients and lists. It may take time for all recipients to receive the email, but sit tight — it won’t take long…"
                  }
                  confirm={"Ok, got it"}
                  onConfirm={() => {
                    closeSendConfirmationBox();
                    closeOverlay();
                    redirectAfterSend();
                  }}
                />
              )}
              <div className="first-row padding-bottom-sm">&nbsp;</div>
              <div className="second-row padding-bottom-md padding-bottom-tab-md">
                <h4>
                  Publish
                  <br />
                  Email
                </h4>
                <div className="scheduale-switch">
                  <Space>
                    <span className="active-text">Test Email</span>
                    <Switch
                      checkedChildren="On"
                      unCheckedChildren="Off"
                      checked={values.isTestEmail}
                      onChange={handleTestSwitch}
                      className={"toggle-schedule"}
                    />
                  </Space>
                </div>
              </div>
            </div>
            <div className="curated-list">
              {sessionStorage.getItem("callback_success") === "false"
                ? showCallbackError && (
                    <Message
                      type="error"
                      label="Outlook authentication failed"
                      callToAction={() => {
                        setTimeout(() => {
                          if (showCallbackError) {
                            setShowCallbackError(false);
                          }
                        }, 3000);
                        return null;
                      }}
                    />
                  )
                : null}
              {!outlookSuccessInfoMessage && emailWithOutlookLoading ? (
                <Message
                  type="info"
                  label="Connecting..."
                  message="Please wait while we connect to your Outlook account."
                  outlookSuccess={true}
                  // uncomment the below function to perform action or make a call back
                  // callToAction={() => {
                  // setTimeout(() => {}, 3000);
                  // return null;
                  // }}
                />
              ) : null}
              {sessionStorage.getItem("callback_success") === "true" || isAuthorized
                ? outlookSuccessInfoMessage && (
                    <Message
                      type="info"
                      label="You’re publishing with Outlook"
                      message="Don’t want to send with Outlook?"
                      outlookSuccess={true}
                      setEmailWithOutlookConfirmation={setEmailWithOutlookConfirmation}
                      // uncomment the below function to perform action or make a call back
                      // callToAction={() => {
                      // setTimeout(() => {}, 3000);
                      // return null;
                      // }}
                    />
                  )
                : null}

              <div id="senderNameDivId"></div>
              <Input
                label="Sender Name *"
                labelProps={{ fontFamily: "'Roboto Slab', serif;" }}
                name="senderName"
                onChange={handleSenderNameChange}
                error={errors.senderName ? "Sender Name cannot be empty!" : false}
                // onFocus={true} // onFocus expects a function but is passed a bool type
                type="text"
                placeholder="Start typing the name of a sender person"
                value={senderName}
                // readOnly={editionStatus === "Sent"}
                disabled={
                  sessionStorage.getItem("callback_success") === "true" || isAuthorized || editionStatus === "Sent"
                }
                onBlur={() => {
                  if (publishData.sender_name !== values.senderName) {
                    savePublishState();
                  }
                }}
              />
              <div id="emailSubjectDivId"></div>
              <Input
                label="Enter Email Subject *"
                labelProps={{ fontFamily: "'Roboto Slab', serif;" }}
                name="emailSubject"
                onChange={handleInputChange}
                // onFocus={true}
                type="text"
                placeholder="Start typing your email subject here…"
                error={errors.emailSubject ? "Email Subject cannot be empty!" : false}
                value={subjectRef}
                // readOnly={editionStatus === "Sent"}
                disabled={editionStatus === "Sent"}
                onBlur={(e: any) => {
                  updateEmailNameForBrowserEmailList(getQueryEmailId(), e.target.value);
                  if (publishData.subject !== subjectRef) {
                    savePublishState();
                  }
                }}
              />
              <div className="input-box">
                <label className="publish-label">Send From: *</label>
                {sessionStorage.getItem("callback_success") === "true" || isAuthorized ? (
                  <Input
                    labelProps={{ removeMargin: true }}
                    value={isAuthorized ? userEmail : outlookSenderEmailAddress}
                    style={{ width: "100%" }}
                    disabled={true}
                  ></Input>
                ) : (
                  <>
                    {errors.sendFrom ? (
                      <span className={"error-msg-span"}>Send from field is mandatory</span>
                    ) : null}
                    <div id="SendFromDivId"></div>
                    <Select
                      defaultValue="Select Email"
                      className={errors.sendFrom ? "error-select-publish" : ""}
                      style={{ width: "100%" }}
                      onChange={handleSendFrom}
                      value={values.sendFrom}
                      disabled={disableSendFrom}
                      popupClassName="Send-From-Publish-Email"
                      getPopupContainer={() => document.querySelectorAll(".curated-list")[0] as HTMLElement}
                      onBlur={(e: any) => {
                        if (publishData.selected_sender !== values.sendFrom) {
                          savePublishState();
                        }
                      }}
                      listHeight={140}
                    >
                      {publishData.sender_emails.map((publishEmail, index) => (
                        <Option key={index + 1} value={publishEmail.domain}>
                          {publishEmail.domain}
                        </Option>
                      ))}
                    </Select>
                    <label className="same-email">
                      <Checkbox
                        name="sameReplyTo"
                        value={values.sameReplyTo}
                        onChange={handleCheckbox}
                        checked={values.sameReplyTo}
                      />
                      Use same email as my Reply-to address
                    </label>
                  </>
                )}
              </div>
              {!values.sameReplyTo && (
                <div id="replyToDivId" className="input-box">
                  {/* toggle d-none class to view right sidebar */}
                  <label className="publish-label">Reply-To: *</label>
                  {errors.replyTo ? (
                    values.replyTo === "" ? (
                      <span className={"error-msg-span"}>Reply-to field is mandatory</span>
                    ) : (
                      <span className={"error-msg-span"}>Enter valid email</span>
                    )
                  ) : null}

                  <Input
                    className={errors.replyTo || !replyToEmailCheck ? "error-select" : ""}
                    type="email"
                    name="Reply-to"
                    placeholder="Type email here…"
                    error={errors.replyTo ? " " : ""}
                    value={values.replyTo}
                    onChange={(e: any) => {
                      handleReplyTo(e.target.value);
                      if (validateEmailAddress(e.target.value)) setReplyToEmailCheck(true);
                      else setReplyToEmailCheck(false);
                    }}
                    onBlur={() => {
                      if (!values.sameReplyTo && values.replyTo !== publishData.reply_to_email) {
                        savePublishState();
                      }
                    }}
                  />
                </div>
              )}
              <div
                className={`input-with-upload input-box ${sendToObjArray.length > 0 ? "contact-selected" : ""}`}
              >
                <div id="sendToDivId" className="d-flex align-items-center justify-content-between">
                  <label className="publish-label">Send To: *</label>

                  <span className="publish-label">
                    <span className="test me-2" onClick={importListFunc}>
                      Import
                    </span>
                    <span className="test" onClick={() => handleOnClickBrowsePeople()}>
                      Browse
                    </span>
                  </span>
                </div>

                <Select
                  mode="multiple"
                  open={openStateDropdown}
                  notFoundContent={null}
                  className={errors.isSendToFieldEmpty ? "error-select-publish" : ""}
                  getPopupContainer={() => document.querySelectorAll(".curated-list")[0] as HTMLElement}
                  onBlur={(e: any) => {
                    setOpenStateDropdown(false);
                    setErrors({ ...errors, isSendToFieldEmpty: false, enterValidEmail: false });
                    let value = e.target.value;
                    if (value.includes(",")) handleMultipleInputs(value);
                    else {
                      if (validateEmail(value)) {
                        handleSendToUpdated({ value, label: value });
                      }
                    }
                    setSendToSearchValue("");
                    // if(e.target.value)
                  }}
                  onKeyDown={(e: any) => {
                    handleKeyDown(e);
                  }}
                  filterOption={false}
                  onSearch={handleSendToSearchValue}
                  searchValue={sendToSearchValue}
                  allowClear
                  clearIcon={
                    <span
                      style={{
                        width: "100px !important",
                        height: "100px !important",
                        fontSize: 12,
                        display: "block",
                      }}
                    >
                      Clear All
                    </span>
                  }
                  onClear={handleOnClear}
                  style={{ width: "100%" }}
                  placeholder="Please select"
                  tagRender={tagRender}
                  labelInValue={true}
                  value={sendToObjArray}
                  onSelect={(e: any) => handleSendToUpdated(e)}
                >
                  {suggestedEmailsLoading ? (
                    <>
                      <Option disabled={true} value={"Fetching"}>
                        Fetching...
                      </Option>
                    </>
                  ) : suggestedEmailDropdown.length > 0 ? (
                    suggestedEmailDropdown
                  ) : searchErrorMessage ? (
                    <>
                      <Option disabled={true} value={"No Data"}>
                        No Match Found!
                      </Option>
                    </>
                  ) : (
                    <>
                      <Option disabled={true} value={"No Data"}>
                        No Match Found!
                      </Option>
                    </>
                  )}
                </Select>
              </div>
              <div className={`recipient-text-style ${values.isTestEmail ? "disabled" : ""}`}>
                <Checkbox
                  value={recipientCheckBoxState}
                  checked={recipientCheckBoxState}
                  onChange={handleRecipientCheckBox}
                  name="addRecipientsCheckbox"
                />
                <span>{`Include Campaign Audience (${
                  recipientCountState === "fetching failed"
                    ? "fetching failed"
                    : recipientCountState + " " + "active recipients"
                })`}</span>{" "}
                <span className={`view-recipients`} onClick={() => openRecipientsSidebar()}>
                  {"view"}
                </span>
              </div>
              {errors.isSendToFieldEmpty && !errors.enterValidEmail ? (
                <span className={"error-msg-span padding-adjust"}>Send to field should not be empty</span>
              ) : errors.enterValidEmail && !errors.isSendToFieldEmpty ? (
                <span className={"error-msg-span padding-adjust"}>Enter valid email</span>
              ) : errors.enterValidEmail && errors.isSendToFieldEmpty ? (
                <span className={"error-msg-span padding-adjust"}>Enter valid email</span>
              ) : null}
              {/* {errors.enterValidEmail ? (
              <span className={"error-msg-span padding-adjust"}>Enter valid email</span>
            ) : errors.isSendToFieldEmpty && !errors.enterValidEmail ? (
              <span className={"error-msg-span padding-adjust"}>Send to field should not be empty</span>
            ) : null} */}
              {sessionStorage.getItem("callback_success") === "true" || isAuthorized ? null : (
                <EmailSchedulingWrapper>
                  {isInternalUser && <div className={"heading"}>Send Options:</div>}
                  {errors.pastScheduleDateCheck ? (
                    <span className={"error-msg-span error-msg-bottom-mrg"}>Select a future date</span>
                  ) : null}
                  <div className={"send-options-content"}>
                    <div className={"d-flex flex-column scheduling-details"}>
                      {isInternalUser ? (
                        <div className="d-flex flex-row justify-content-between">
                          <div className={"sub-heading"}>Send Email With:</div>
                          {errors.sendWith && (
                            <span className={"error-msg-span error-msg-bottom-mrg"}>Send with is mandatory</span>
                          )}
                          <br />
                          <Radio.Group
                            name="sendWith"
                            onChange={handleChangeRadio}
                            value={values.sendWith}
                            className={`d-flex flex-row ${errors.sendWith ? "error-select" : ""}`}
                          >
                            <Radio className={"email-vendor"} value={"1"}>
                              Amazon
                            </Radio>
                            <Radio className={"email-vendor"} value={"2"}>
                              SendGrid
                            </Radio>
                          </Radio.Group>
                        </div>
                      ) : null}
                      <div className="d-flex justify-content-between">
                        <span className="sub-heading ">Schedule Send:</span>
                        <div className={"d-flex"}>
                          {/* <Space> */}
                          <Switch
                            checkedChildren="On"
                            unCheckedChildren="Off"
                            checked={scheduleSend}
                            onChange={toggleScheduleSend}
                          />
                          {/* </Space> */}
                          <span className="text-before-date">Sends </span>
                          <span className="date-in-text">
                            {scheduleSend ? formatedText(selectedDateState) : "Immediately"}{" "}
                          </span>
                        </div>
                      </div>
                    </div>
                    {scheduleSend && (
                      <>
                        <div className={"left-margined-portion"}>
                          <Space>
                            <span
                              className="text-before-date-time-selectors"
                              style={{ minWidth: "20px", display: "inline-block" }}
                            >
                              on
                            </span>
                            <Select
                              suffixIcon={DropDownArrowIcon}
                              onSelect={monthChangeHandler}
                              value={selectedDateState?.month}
                              getPopupContainer={() =>
                                document.querySelectorAll(".curated-list")[0] as HTMLElement
                              }
                              listHeight={170}
                            >
                              {monthsCollection.map((item, index) => (
                                <Option value={item}>{item}</Option>
                              ))}
                            </Select>

                            <Select
                              suffixIcon={DropDownArrowIcon}
                              onSelect={dayChangeHandler}
                              value={selectedDateState?.day}
                              getPopupContainer={() =>
                                document.querySelectorAll(".curated-list")[0] as HTMLElement
                              }
                              listHeight={170}
                            >
                              {dayCollection.map((item, index) => (
                                <Option value={(index + 1).toString()}>{item}</Option>
                              ))}
                            </Select>

                            <Select
                              suffixIcon={DropDownArrowIcon}
                              onSelect={yearChangeHandler}
                              value={selectedDateState?.year}
                              getPopupContainer={() =>
                                document.querySelectorAll(".curated-list")[0] as HTMLElement
                              }
                              listHeight={170}
                            >
                              {yearCollection.map((item, index) => (
                                <Option value={item}>{item}</Option>
                              ))}
                            </Select>
                          </Space>
                        </div>
                        <div className={"left-margined-portion"}>
                          <Space>
                            <span
                              className="text-before-date-time-selectors"
                              style={{ minWidth: "20px", display: "inline-block" }}
                            >
                              at
                            </span>
                            <Select
                              suffixIcon={DropDownArrowIcon}
                              onSelect={hourChangeHandler}
                              value={selectedDateState?.hour}
                              getPopupContainer={() =>
                                document.querySelectorAll(".curated-list")[0] as HTMLElement
                              }
                              listHeight={170}
                            >
                              {hoursCollection.map((item, index) => (
                                <Option value={item}>{item}</Option>
                              ))}
                            </Select>

                            <Select
                              suffixIcon={DropDownArrowIcon}
                              onSelect={minuteChangeHandler}
                              value={selectedDateState?.minute}
                              getPopupContainer={() =>
                                document.querySelectorAll(".curated-list")[0] as HTMLElement
                              }
                              listHeight={170}
                            >
                              {minutesCollection.map((item, index) => (
                                <Option value={item}>{item}</Option>
                              ))}
                            </Select>

                            <Select
                              suffixIcon={DropDownArrowIcon}
                              onSelect={amPmChangeHandler}
                              value={selectedDateState?.period}
                              getPopupContainer={() =>
                                document.querySelectorAll(".curated-list")[0] as HTMLElement
                              }
                              listHeight={170}
                            >
                              {["am", "pm"].map((item, index) => (
                                <Option value={item}>{item}</Option>
                              ))}
                            </Select>
                          </Space>
                        </div>
                      </>
                    )}
                  </div>
                </EmailSchedulingWrapper>
              )}
              <div className="divider-row">
                <Divider />
              </div>
              {isInternalUser && (
                <EmailTypeWrapper>
                  <div className="d-flex justify-content-between">
                    <div className="heading-type ">Email Type:</div>
                    <div className={"d-flex flex-1"}>
                      <Space>
                        <Switch
                          checkedChildren="On"
                          unCheckedChildren="Off"
                          checked={values.isXiqEvent}
                          onChange={handleWebinarSwitch}
                          className={"toggle-schedule"}
                        />
                        <span className="text-style">Webinar</span>
                      </Space>
                      {/* setting visibility hidden to maintain the space */}
                      <Space style={{ visibility: "hidden" }}>
                        <Switch
                          checkedChildren="On"
                          unCheckedChildren="Off"
                          checked={values.isTestEmail}
                          onChange={handleTestSwitch}
                          className={"toggle-schedule"}
                        />
                        <span className="text-style">Test</span>
                      </Space>
                    </div>
                  </div>
                </EmailTypeWrapper>
              )}
              <div className="compliance-check">
                <label>
                  <Checkbox
                    value={values.emailComplianceCheck}
                    onChange={handleCheckbox}
                    name="emailComplianceCheck"
                  />
                  Email Compliance Check
                </label>
                <p>
                  I have read xiQ's Permission Based Marketing Policy and confirm that any emails sent to these
                  prospects will be in compliance.
                </p>
              </div>
            </div>
            <div className="bottom-floating-action publish-footer">
              <Space align="end">
                <SmallButton
                  disabled={
                    !values.emailComplianceCheck ||
                    (sendToObjArray?.length === 0 && recipientCountState === 0) ||
                    sendDisabled ||
                    disablePublishBtn ||
                    openPublishedConfirmation ||
                    showRecipientAsider
                  }
                  onClick={handlePublishSubmit}
                  title={scheduleSend ? (checkScheduleUpdate() ? "Update Schedule" : "Schedule") : "Publish"}
                />
              </Space>
            </div>
          </Wrapper>
        )}
      </FloatingSidebarTwo>
      {emailWithOutlookConfirmation && (
        <ConfirmDialog
          confirm="Yes"
          cancel="Cancel"
          onConfirm={() => emailWithOutlookConfirmationHandler("onConfirm")}
          onCancel={() => emailWithOutlookConfirmationHandler("onCancel")}
          title={null}
          titleQuestion="Are you sure you don’t want to send with Outlook?"
        />
      )}
      {showConfirmDialog && (
        // <DialogWrapper>
        //   <div className="back-overlay" />
        <ConfirmDialog
          cancel="Cancel"
          confirm={outlookRouteReducerState && mailToCount > outlookRemainingEmails ? `Okay` : `Confirm`}
          onConfirm={sendEmailComfirmHandler}
          onCancel={() => {
            setShowConfirmDialog(false);
            setSendDisabled(false);
          }}
          title={null}
          titleQuestion={
            outlookRouteReducerState && mailToCount > outlookRemainingEmails
              ? `With Outlook you can send email to max 500 recipients in 24 Hours`
              : `Are you sure you want to send this mail to ${mailToCount} recipients?`
          }
          after={true}
        />
        // </DialogWrapper>
      )}
      {showDraftWorkflowPopup && (
        <ConfirmDialog
          cancel="Cancel"
          confirm="View the Workflow"
          onCancel={() => setShowDraftWorkflowPopup(false)}
          maxLines={5}
          onConfirm={() => {
            history.push(`/automation/details?workflow_id=${workflowId}&from=publish`);
            setShowDraftWorkflowPopup(false);
          }}
          title={null}
          titleQuestion={
            workflowEmailStatus === "wf_graph"
              ? `This email cannot be
          published as it is set to
          go out automatically in
          the workflow.`
              : `This email cannot be published as Workflow is not active. Please activate the workflow to publish this email.`
          }
        />
      )}
      {showRecipeintsZeroDialog && (
        // <DialogWrapper>
        //   <div className="back-overlay" />
        <ConfirmDialog
          confirm="Okay"
          onConfirm={() => {
            setShowRecipientsZeroDialog(false);
            setSendDisabled(false);
          }}
          title={null}
          titleQuestion={`It looks like your added contacts are either invalid or unsubscribed. Please add valid contacts`}
        />
        // </DialogWrapper>
      )}
      {showExportSuccessPopup && (
        <WrapperNotice>
          <div className="back-overlay" />
          <ConfirmDialog
            confirm="Ok"
            onConfirm={handleConfirmExportSalesXPopup}
            onCancel={handleConfirmExportSalesXPopup}
            title="Widgets exported successfully"
            titleQuestion={null}
          />
        </WrapperNotice>
      )}
      <ImportListFlow
        inPublishEmail={true}
        showImportListAsider={showImportListAsider}
        setShowImportListAsider={setShowImportListAsider}
        handleAddPeople={handleAddPeople}
      />
    </>
  );
};

export default PublishContent;

const Wrapper = styled.div`
  padding: 0px 40px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    padding: 0px 30px;
  }
  .ant-select-clear {
    right: 41px;
    top: unset;
    bottom: 5px;
    white-space: nowrap;
    color: #1ec252;
    font-family: Roboto;
  }
  .icon {
    margin: 0 5px;
    padding-right: 5px;
    border-right: 1px solid #dadada;
    svg g {
      fill: #001760;
    }

    &.contact-icon {
      svg g {
        fill: #1ec252;
      }
    }
  }
  .icon-cross {
    cursor: pointer;
    margin-left: 5px;
    z-index: 3;
    svg {
      circle {
        fill: #001760;
      }
    }
    &.success-cross {
      svg {
        circle {
          fill: #1ec252;
        }
      }
    }
  }
  .curated-list {
    height: calc(100vh - 365px) !important;
    position: relative;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: calc(100vh - 290px) !important;
    }
  }
  .error:not(input) {
    color: #f83434;
    font-size: small;
    font-family: "Roboto Slab", serif;
    margin: 3px 11px;
  }
  .error-msg-span {
    color: #db3725;
    font-style: italic;
    font-size: 14px;
    text-align: right;
    float: right;
    margin-top: 1px;
    margin-right: 5px;
    font-family: "robotomedium_italic";
    font-weight: normal;
    padding-top: 40px;
    &.error-msg-bottom-mrg {
      padding-top: 0px;
    }
  }
  .padding-adjust {
    padding-top: 0px;
  }
  .recipient-text-style {
    margin-top: 8px;
    margin-left: 3px;
    color: #5e7079;
    font-size: 16px;
    font-family: "robotomedium";
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 12px;
    }
    .view-recipients {
      color: #27ce0a;
      text-decoration: underline;
      cursor: pointer;
    }
  }
  .disabled {
    pointer-events: none;
    /* for "disabled" effect */
    opacity: 0.5;
  }
  .test {
    font-weight: bold;
    font-size: 12px;
    color: #27ce0a;
    border: 1px solid #27ce0a;
    padding: 2px 8px;
    border-radius: 6px;
    cursor: pointer;
    letter-spacing: 1px;
    margin-bottom: -28px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 9px;
      border-radius: 4px;
    }
    &.active {
      color: #fff;
      border: 1px solid #27ce0a;
      background-color: #27ce0a;
    }
  }
  /* .select-container {
    position: relative;
  } */
  .input-box#replyToDivId {
    label:not(.publish-label) {
      margin: 0;
    }
  }
  .divider-row {
    .ant-divider-horizontal {
      margin: 0px 0 16px 0;
    }
  }
  .input-with-upload {
    .ant-tag {
      white-space: nowrap;
      word-break: break-word;
      overflow: hidden;
      text-overflow: ellipsis;
      justify-content: start;
      display: block !important;
      padding-right: 25px !important;
      position: relative;
      .icon-cross {
        position: absolute;
        right: 5px;
        top: 2px;
        z-index: 3;
      }
      .edit-option {
        position: absolute;
        top: 0;
        left: 0;
        z-index: -2;
        opacity: 0;
        visibility: hidden;
        background-color: #696969;
        color: #fff;
        font-size: 14px;
        font-weight: 500;
        text-align: center;
        display: block;
        width: 100%;
        font-family: "Roboto", sans-serif;
        padding: 3px 0;
        transition: 0.5s;
        padding-right: 12px;
        svg {
          margin-right: 5px;
          vertical-align: baseline;
        }
      }
      .list-item {
        &:hover {
          .edit-option {
            opacity: 0.9;
            visibility: visible;
            z-index: 2;
          }
          .icon-cross {
            transition: 0.5s;
            svg {
              circle {
                transition: 0.5s;
                fill: #fff;
              }
            }
            polygon {
              transition: 0.5s;
              fill: #696969;
            }
          }
        }
      }
    }

    &.contact-selected {
      .ant-select-selector {
        padding-bottom: 15px !important;
      }
    }
  }
  .ant-select-item-option-content {
    display: flex;
    justify-content: space-between;
    .dropdownValue {
      margin-bottom: 0;
      /* max-width: 75%; */
    }
    .disabled {
      opacity: 0.4;
    }
  }
  .unsubText {
    background: #ffe2e4;
    border: 1px solid #dadada;
    border-radius: 10px;
    font-family: "Roboto", sans-serif;
    font-weight: 500;
    font-size: 10px;
    line-height: 11px;
    letter-spacing: -0.236842px;
    color: #f5222d;
    min-width: 75px;
    min-height: 20px;
    padding: 5px 15px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }
  .bounceColor {
    color: rgb(255, 149, 49) !important;
    background: rgb(255, 149, 49, 0.2) !important;
  }
`;

const EmailSchedulingWrapper = styled.div`
  width: 373px;
  /* height: 292; */
  margin-top: 16px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    width: 221px;
    /* height: 292; */
    /* margin-top: 18px; */
  }
  .heading {
    color: #5e7079;
    font-size: 20px;
    font-weight: 600;
    font-family: "Roboto Slab", serif;
    display: inline-block;

    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 12px;
    }
  }
  .sub-heading {
    color: #5e7079;
    font-size: 16px;
    font-family: "Roboto", sans-serif;
    font-weight: 500;
    min-width: 110px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 11px;
      min-width: 90px;
      padding-top: 3px;
    }
  }
  .toggle-schedule {
    width: 36px;
    height: 18px;
  }
  .ant-input-number {
    width: 58px;
    border-radius: 8px;
    .ant-input-number-handler-wrap {
      opacity: 1;
      margin-right: 3px;
    }
    .ant-input-number-input {
      color: #7e7e7e;
      font-size: 16px !important;
      font-weight: 500;
      font-family: "Roboto";
    }
  }
  .ant-input-number-input-wrap {
    .ant-input-number-input {
      font-size: 16px !important;
      width: 54px;
      height: 27px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 11px;
      }
    }
  }

  .date-in-text {
    margin-left: 5px;
    color: #424b50;
    font-size: 16px;
    font-weight: 500;
    font-family: "Roboto";
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 11px;
    }
  }
  .text-before-date {
    color: #5e7079;
    font-size: 16px;
    font-weight: 500;
    font-family: "Roboto";
    margin-left: 22px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 11px;
      margin-left: 10px;
    }
  }
  .text-before-date-time-selectors {
    color: #5e7079;
    font-size: 18px;
    font-family: "Roboto";
    font-weight: 500;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 12px;
    }
  }
  .send-options-content {
  }
  .email-vendor {
    font-family: "Roboto";
    font-size: 16px;
    font-weight: 500;
    color: #5e7079;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 11px;
    }
  }
  .scheduling-details {
    min-height: 110px;
    justify-content: space-evenly;
  }
  .ant-select {
    .ant-select-selector {
      height: 38px !important;
      width: 85px !important;
      font-size: 16px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: 27px !important;
        width: 70px !important;
        font-size: 11px !important;
      }
    }
  }
  .ant-switch-inner {
    /* font-size: 14px; */
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 10px;
      line-height: 14px;
    }
  }
  .ant-switch {
    /* height: 26px; */
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: 19px;
    }
  }
  .ant-switch-handle {
    /* width: 22px;
    height: 22px; */
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      width: 15px;
      height: 15px;
    }
  }
  .left-margined-portion {
    margin-left: 44px;
    margin-bottom: 14px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-left: 30px;
      margin-bottom: 10px;
    }
  }
`;

const WrapperNotice = styled.div`
  z-index: 17;
  top: 50%;
  left: 50%;
  position: fixed;
  .back-overlay {
    position: fixed;
    background: #000;
    opacity: 0.6;
    height: 100vh;
    width: 100vw;
    top: 0;
    left: 0;
  }
`;
const DialogWrapper = styled.div`
  z-index: 17;
  top: 50%;
  left: 50%;
  position: fixed;
  .back-overlay {
    position: fixed;
    background: grey;
    opacity: 0.2;
    height: 100vh;
    width: 100vw;
    top: 0;
    left: 0;
  }
`;

const EmailTypeWrapper = styled.div`
  width: 373px;
  /* height: 292; */
  /* margin-top: 16px; */
  margin-bottom: 10px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    width: 221px;
    /* height: 292; */
    /* margin-top: 18px; */
  }
  .heading-type {
    color: #5e7079;
    font-size: 16px;
    font-family: "Roboto", sans-serif;
    font-weight: 500;
    min-width: 110px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 11px;
      min-width: 90px;
      padding-top: 3px;
    }
  }
  .text-style {
    color: #5e7079;
    font-size: 16px;
    font-weight: 500;
    font-family: "Roboto";
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 11px;
      margin-left: 10px;
    }
  }
  /* following code is copied to make UI similar to the above toggle button */
  .ant-switch-inner {
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 10px;
      line-height: 14px;
    }
  }
  .ant-switch {
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: 19px;
    }
  }
  .ant-switch-handle {
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      width: 15px;
      height: 15px;
    }
  }
  .left-margined-portion {
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-left: 30px;
      margin-bottom: 10px;
    }
  }
`;
